import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useUser } from "@/contexts/UserContext";
import { formatPhone } from "@/utils/helper";
import { TrackRecord, type Employee } from "@/utils/types";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import { format } from "date-fns-tz";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import BranchRequiredWrapper from "../../../banners/BranchRequiredWrapper";
import PageLoader from "../../../loader/PageLoader";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

const ViewEmployeeTracking = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { id: employeeId } = useParams<{ id: string }>();
  const [axiosInstance, loading, isFetching] = useAxiosInstance();
  const [employee, setEmployee] = useState<Employee>();
  const [trackRecords, setTrackRecords] = useState<TrackRecord[]>([]);
  const navigate = useNavigate();

  const formatDate = (date?: string) =>
    date
      ? format(date, "PP", {
          timeZone: "America/Chicago",
        })
      : null;

  const formatTime = (date?: string) =>
    date
      ? format(date, "kk:mm", {
          timeZone: "America/Chicago",
        })
      : null;

  const formatDuration = (duration: string | undefined) => {
    if (!duration) return "";
    const timePart = duration.split(".")[0];
    const [hours, minutes] = timePart.split(":");
    return `${hours}:${minutes}`;
  };

  useEffect(() => {
    const fetchEmployeeDetails = async () => {
      if (loading || !axiosInstance) return;
      try {
        const [employeeRes, employeeTrackRes] = await Promise.all([
          axiosInstance.get(`/api/v1/employee/${employeeId}`),
          axiosInstance.get(`/api/v1/employee-tracking/?employee=${employeeId}`),
        ]);
        setEmployee(employeeRes.data);
        setTrackRecords(employeeTrackRes.data?.results);
        console.log("Track: ", employeeTrackRes.data);
      } catch (error) {
        toast.error("Failed to fetch employee tracking records");
        console.error(error);
      }
    };
    if (user?.selectedBranch?.id) {
      fetchEmployeeDetails();
    }
  }, [axiosInstance, employeeId, loading, user]);

  return (
    <PageLoader isFetching={isFetching}>
      <BranchRequiredWrapper>
        <h1 className="mb-4 text-xl font-bold">View Employee</h1>
        <div className="flex flex-col space-y-6 rounded-lg bg-white p-4 shadow">
          <div id="printable">
            <div>
              <div className="mb-8 grid grid-cols-2 gap-4">
                <div>
                  <span className="font-semibold">{t("Name")}:</span>{" "}
                  {employee?.first_name} {employee?.last_name}
                </div>
                <div>
                  <span className="font-semibold">{t("Employee Pin")}:</span>{" "}
                  {employee?.employee_pin}
                </div>
                <div>
                  <span className="font-semibold">{t("Phone")}:</span>{" "}
                  {formatPhone(employee?.phone.substring(2) || "")}
                </div>
                <div>
                  <span className="font-semibold">{t("Email")}:</span>{" "}
                  <u>{employee?.email}</u>
                </div>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Clock In Time</TableHead>
                  <TableHead>Clock Out Time</TableHead>
                  <TableHead>Duration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trackRecords?.map((trackRecord) => (
                  <TableRow key={trackRecord.id}>
                    <TableCell>{formatDate(trackRecord.clock_in)}</TableCell>
                    <TableCell>{formatTime(trackRecord.clock_in)}</TableCell>
                    <TableCell>{formatTime(trackRecord.clock_out)}</TableCell>
                    <TableCell>
                      {formatDuration(trackRecord.session_duration?.toString())}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex justify-end space-x-4">
            <Button
              onClick={() => navigate("/employee")}
              variant="secondary"
              className="bg-gray-500 text-white hover:text-gray-600"
              type="button"
            >
              Close
            </Button>
            {/* <Button
              variant="default"
              className="bg-red-500 text-white hover:bg-red-600"
              type="button"
              onClick={() => {
                const printContents =
                  document.querySelector("#printable")!.innerHTML;
                if (printContents) {
                  document.body.innerHTML = printContents;
                  window.print();
                  window.location.reload();
                }
              }}
            >
              Print Report
            </Button> */}
          </div>
        </div>
      </BranchRequiredWrapper>
    </PageLoader>
  );
};

export default ViewEmployeeTracking;
